import { Selection, EnterElement, ScaleLinear, ScaleTime } from "d3"
import { D3OneToOneRenderable } from "./D3OneToOneRenderable"
import { D3LinearYAxis } from "./D3LinearYAxis"
import { MarginedBoundingBox } from "../../Types/MarginedBoundingBox"
import { D3SummaryValues } from "./D3SummaryValues"
import { TimeSeriesPageManager } from "../../Data/TimeSeriesPageManager"
import { ReactCallbacks } from "../../Types/ReactCallbacks"
import { MobergFontFamily, MobergFontSize } from "../../../../../../Moberg"
import { valueOrDefault } from "../../utils"

type BoxPlotData = {
    mean: number
    min: number
    q1: number
    median: number
    q3: number
    max: number
}

export type D3BoxPlotConfig = {
    color: string
    boundingBox: MarginedBoundingBox
    scale: ScaleLinear<any, any>
    yLabel: string
    viewScale: ScaleTime<any, any, any>
    title: string
    precision: number
    values: number[]
}

export class D3BoxPlot extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3BoxPlotConfig> {
    private boxPlotClassName = "d3-box-plot"
    private boxClassName = "d3-box-plot-iqr-box"
    private medianLineClassName = "d3-box-plot-median-line"
    private bottomWhiskerClassName = "d3-box-plot-bottom-whisker"
    private topWhiskerClassName = "d3-box-plot-top-whisker"
    private topHorizontalLineClassName = "d3-box-plot-top-horizontal-line"
    private bottomHorizontalLineClassName = "d3-box-plot-horizontal-line"
    private summaryValuesContainerClassName = "d3-box-plot-summary-values-container"
    private titleClassName = "d3-box-plot-title"
    private titleSpace = 10
    private isCompressedView: boolean = false
    private nonCompressedViewXOffset: number = 0
    private boxWidth: number = 0
    private transitionDurationMs: number = 500
    private pageManager: TimeSeriesPageManager<any>
    private boxPlotData: BoxPlotData = {
        min: NaN,
        max: NaN,
        q1: NaN,
        q3: NaN,
        median: NaN,
        mean: NaN
    }

    private axis?: D3LinearYAxis
    private summaryValues?: D3SummaryValues

    private colorSchemeObserver: MutationObserver

    constructor(root: SVGGElement, config: D3BoxPlotConfig, className: string, reactCallbacks: ReactCallbacks<any>, pageManager: TimeSeriesPageManager<any>) {
        super(root, config, className, reactCallbacks)
        this.pageManager = pageManager

        this.colorSchemeObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.attributeName === "data-color-scheme") {
                    this.updateChildren()
                    this.render()
                    break
                }
            }
        })
        this.colorSchemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-color-scheme"] })

        this.updateDerivedState()
        this.render()
    }

    public destroy() {
        this.colorSchemeObserver.disconnect()
    }

    protected updateDerivedState(): void {
        this.config.scale.range([this.config.boundingBox.height, 0])
        this.boxWidth = this.config.boundingBox.width/8
        this.isCompressedView = this.config.boundingBox.height < 200
        this.nonCompressedViewXOffset = Math.max(0, this.config.boundingBox.width/3 - 40)
        this.updateBoxPlotData()
        this.updateChildren()
    }

    protected enter = (newElements: Selection<EnterElement, D3BoxPlotConfig, any, any>): Selection<SVGGElement, D3BoxPlotConfig, any, any> => {
        const boxPlotGroup = newElements
            .append("g")
            .attr("class", this.className)

        boxPlotGroup
            .append("text")
            .attr("class", this.titleClassName)
            .text(this.config.title)
            .attr("text-anchor", "middle")
            .style("font-family", MobergFontFamily.HEADER)
            .style("font-size", MobergFontSize.LARGE)
            .style("fill", "var(--color-chart-axis-text, black)")
            .attr("transform", `translate(${this.config.boundingBox.width / 2}, ${-this.titleSpace})`)

        const boxPlot = boxPlotGroup
            .append("g")
            .attr("class", this.boxPlotClassName)
            .attr("transform", `translate(${this.isCompressedView ? 0 : this.nonCompressedViewXOffset}, 0)`)

        const summaryValuesGroup = boxPlotGroup
            .append("g")
            .attr("class", this.summaryValuesContainerClassName)
            .attr("transform", `translate(${this.config.boundingBox.width / 4 + (this.isCompressedView ? 0 : this.nonCompressedViewXOffset) }, 0)`)

        // Draw box
        boxPlot.append("rect")
            .attr("class", this.boxClassName)
            .attr("x", this.boxWidth/2)
            .attr("y", this.config.scale(this.boxPlotData.q3))
            .attr("width", this.boxWidth)
            .attr("height", valueOrDefault(this.config.scale(this.boxPlotData.q1) - this.config.scale(this.boxPlotData.q3), 0))
            .attr("stroke", "var(--color-chart-axis-text, black)")
            .attr("fill", this.config.color)

        // Draw median line
        boxPlot.append("line")
            .attr("class", this.medianLineClassName)
            .attr("x1", 0)
            .attr("x2", this.boxWidth)
            .attr("y1", this.config.scale(this.boxPlotData.median))
            .attr("y2", this.config.scale(this.boxPlotData.median))
            .attr("stroke", "var(--color-chart-axis-text, black)")

        // Draw whiskers
        boxPlot.append("line")
            .attr("class", this.topWhiskerClassName)
            .attr("x1", this.boxWidth/2)
            .attr("x2", this.boxWidth/2)
            .attr("y1", this.config.scale(this.boxPlotData.min))
            .attr("y2", this.config.scale(this.boxPlotData.q1))
            .attr("stroke", "var(--color-chart-axis-text, black)")

        boxPlot.append("line")
            .attr("class", this.bottomWhiskerClassName)
            .attr("x1", this.boxWidth/2)
            .attr("x2", this.boxWidth/2)
            .attr("y1", this.config.scale(this.boxPlotData.q3))
            .attr("y2", this.config.scale(this.boxPlotData.max))
            .attr("stroke", "var(--color-chart-axis-text, black)")

        // Horizontal lines on whiskers
        boxPlot.append("line")
            .attr("class", this.bottomHorizontalLineClassName)
            .attr("x1", 3*this.boxWidth/4)
            .attr("x2", 5*this.boxWidth/4)
            .attr("y1", this.config.scale(this.boxPlotData.min))
            .attr("y2", this.config.scale(this.boxPlotData.min))
            .attr("stroke", "var(--color-chart-axis-text, black)")

        boxPlot.append("line")
            .attr("class", this.topHorizontalLineClassName)
            .attr("x1", 3*this.boxWidth/4)
            .attr("x2", 5*this.boxWidth/4)
            .attr("y1", this.config.scale(this.boxPlotData.max))
            .attr("y2", this.config.scale(this.boxPlotData.max))
            .attr("stroke", "var(--color-chart-axis-text, black)")

        boxPlot.each((config, index, nodes) => this.createChildren(config, index, nodes))
        summaryValuesGroup.each((config, index, nodes) => this.createSummaryValuesChildren(config, index, nodes))

        return boxPlotGroup
    }

    protected update = (updatedElements: Selection<SVGGElement, D3BoxPlotConfig, any, any>): Selection<SVGGElement, D3BoxPlotConfig, any, any> => {
        const boxPlotGroup = updatedElements

        boxPlotGroup.select("." + this.boxPlotClassName)
            .attr("transform", `translate(${this.isCompressedView ? 0 : this.nonCompressedViewXOffset}, 0)`)

        boxPlotGroup.select("." + this.titleClassName)
            .attr("transform", `translate(${this.config.boundingBox.width / 2}, ${-this.titleSpace})`)
            .text(this.config.title)

        boxPlotGroup.select("." + this.summaryValuesContainerClassName)
            .attr("transform", `translate(${this.config.boundingBox.width / 4 + (this.isCompressedView ? 0 : this.nonCompressedViewXOffset)}, 0)`)

        const boxPlot = boxPlotGroup.select("." + this.boxPlotClassName)

        // Draw box
        boxPlot.select("." + this.boxClassName)
            .attr("x", this.boxWidth/2)
            .transition()
            .duration(this.transitionDurationMs)
            .attr("y", this.config.scale(this.boxPlotData.q3))
            .attr("width", this.boxWidth)
            .attr("height", valueOrDefault(this.config.scale(this.boxPlotData.q1) - this.config.scale(this.boxPlotData.q3), 0))
            .attr("fill", this.config.color)

        // Draw median line
        boxPlot.select("." + this.medianLineClassName)
            .attr("x1", this.boxWidth/2)
            .attr("x2", 3*this.boxWidth/2)
            .transition()
            .duration(this.transitionDurationMs)
            .attr("y1", this.config.scale(this.boxPlotData.median))
            .attr("y2", this.config.scale(this.boxPlotData.median))

        // Draw whiskers
        boxPlot.select("." + this.topWhiskerClassName)
            .attr("x1", this.boxWidth)
            .attr("x2", this.boxWidth)
            .transition()
            .duration(this.transitionDurationMs)
            .attr("y1", this.config.scale(this.boxPlotData.min))
            .attr("y2", this.config.scale(this.boxPlotData.q1))

        boxPlot.select("." + this.bottomWhiskerClassName)
            .attr("x1", this.boxWidth)
            .attr("x2", this.boxWidth)
            .transition()
            .duration(this.transitionDurationMs)
            .attr("y1", this.config.scale(this.boxPlotData.q3))
            .attr("y2", this.config.scale(this.boxPlotData.max))

        // Horizontal lines on whiskers
        boxPlot.select("." + this.bottomHorizontalLineClassName)
            .attr("x1", 3*this.boxWidth/4)
            .attr("x2", 5*this.boxWidth/4)
            .transition()
            .duration(this.transitionDurationMs)
            .attr("y1", this.config.scale(this.boxPlotData.min))
            .attr("y2", this.config.scale(this.boxPlotData.min))

        boxPlot.select("." + this.topHorizontalLineClassName)
            .attr("x1", 3*this.boxWidth/4)
            .attr("x2", 5*this.boxWidth/4)
            .transition()
            .duration(this.transitionDurationMs)
            .attr("y1", this.config.scale(this.boxPlotData.max))
            .attr("y2", this.config.scale(this.boxPlotData.max))

        this.renderChildren()

        return boxPlotGroup
    }

    private createChildren(config: D3BoxPlotConfig, index: number, nodes: ArrayLike<SVGGElement>) {
        const root = nodes[index]
        this.axis = new D3LinearYAxis(root, { scale: this.config.scale, boundingBox: this.config.boundingBox, label: this.config.yLabel }, this.reactCallbacks)
    }

    private createSummaryValuesChildren(config: D3BoxPlotConfig, index: number, nodes: ArrayLike<SVGGElement>) {
        const root = nodes[index]
        this.summaryValues = new D3SummaryValues(root, this.getSummaryValuesConfig(), "d3-box-plot-summary-values", this.reactCallbacks, this.pageManager, this.config.viewScale)
    }

    private renderChildren() {
        this.summaryValues?.updateConfig(this.getSummaryValuesConfig())
    }

    private updateChildren() {
        this.axis?.updateConfig({ scale: this.config.scale, boundingBox: this.config.boundingBox, label: this.config.yLabel })
        this.summaryValues?.updateConfig(this.getSummaryValuesConfig())
    }

    private getSummaryValuesConfig() {
        const textColor = getComputedStyle(document.documentElement).getPropertyValue("--color-chart-axis-text").trim() || "black"
        return {
            values: [
                {
                    value: this.boxPlotData.mean,
                    name: "Mean",
                    color: textColor,
                    precision: this.config.precision,
                    isVisible: true
                },
                {
                    value: this.boxPlotData.median,
                    name: "Median",
                    color: textColor,
                    precision: this.config.precision,
                    isVisible: true
                },
                {
                    value: this.boxPlotData.q1,
                    name: "Q1",
                    color: textColor,
                    precision: this.config.precision,
                    isVisible: true
                },
                {
                    value: this.boxPlotData.q3,
                    name: "Q3",
                    color: textColor,
                    precision: this.config.precision,
                    isVisible: true
                }
            ],
            x: 5,
            y: 5,
            width: 3*this.config.boundingBox.width / 4 - 10,
            height: this.config.boundingBox.height - 10,
            rows: this.isCompressedView ? 2 : 4,
            columns: this.isCompressedView ? 2 : 1
        }
    }

    public updateBoxPlotData = () => {
        if (this.config.values.length === 0) {
            this.boxPlotData = {
                min: NaN,
                max: NaN,
                mean: NaN,
                median: NaN,
                q1: NaN,
                q3: NaN,
            }
            return
        }

        const sorted = this.config.values.sort((a, b) => a - b)
        const n = sorted.length

        const mean = sorted.reduce((sum, val) => sum + val, 0) / n
        const median = this.getPercentile(sorted, 50)
        const q1 = this.getPercentile(sorted, 25)
        const q3 = this.getPercentile(sorted, 75)
        const min = sorted[0]
        const max = sorted[n-1]
        const iqr = q3 - q1

        this.boxPlotData = {
            min: Math.max(min, q1 - 1.5 * iqr),
            max: Math.min(max, q3 + 1.5 * iqr),
            mean,
            median,
            q1,
            q3,
        }
    }

    private getPercentile(sorted: number[], percentile: number): number {
        const index = (percentile / 100) * (sorted.length - 1)
        const lower = Math.floor(index)
        const upper = Math.ceil(index)

        if (lower === upper) {
            return sorted[lower]
        }

        const weight = index - lower

        return sorted[lower] * (1 - weight) + sorted[upper] * weight
    }
}
